// §6.1 — the runtime skin validator.
//
// It never throws: F13 requires a skin that fails validation to fall back to the house skin
// with an error notification, so the caller needs a result it can inspect, not an exception.
import type { FC } from 'react';
import {
  POLICY_METHOD_NAMES,
  SKIN_COMPONENT_NAMES,
  SKIN_IDS,
  SOUND_IDS,
  TOKEN_NAMES,
  type DialogProps,
  type Skin,
  type TokenName,
  type Win,
} from '@hangar/shared';

/**
 * §6.1's component set, in React terms. `shared` types the components structurally so it can
 * keep its zero-dependency rule (§6.5); this is the same contract with React's return type, so
 * the shell can actually render them. A `UiSkin` is assignable to a `Skin`.
 */
export interface UiSkinComponents {
  DesktopSurface: FC;
  Bar: FC;
  Launcher: FC;
  WindowFrame: FC<{ win: Win }>;
  Dialog: FC<DialogProps>;
  NotificationHost: FC;
  Switcher: FC;
  TrayFlyout: FC;
  LoginScreen: FC;
  LockScreen: FC;
}

export interface UiSkin extends Omit<Skin, 'components'> {
  components: UiSkinComponents;
}

export type SkinProblemKind =
  | 'id'
  | 'label'
  | 'component'
  | 'policy'
  | 'keymap'
  | 'token'
  | 'sound';

export interface SkinProblem {
  kind: SkinProblemKind;
  /** The missing component, token, policy method or sound id. */
  name: string;
  message: string;
}

export type SkinValidation = { ok: true } | { ok: false; problems: SkinProblem[] };

/** Every `--custom-property` declared anywhere in a stylesheet's text. */
export function declaredTokens(css: string): Set<string> {
  const found = new Set<string>();
  // Match a custom-property declaration: `--name:` at a declaration position. Anything inside
  // a `var(--name, …)` reference is followed by `,` or `)`, never `:`, so it cannot match.
  const re = /(^|[;{\s])(--[a-zA-Z0-9-_]+)\s*:/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(css)) !== null) {
    if (m[2]) found.add(m[2]);
  }
  return found;
}

export function missingTokens(css: string): TokenName[] {
  const declared = declaredTokens(css);
  return TOKEN_NAMES.filter((name) => !declared.has(name));
}

export function validateSkin(skin: unknown): SkinValidation {
  const problems: SkinProblem[] = [];
  const push = (kind: SkinProblemKind, name: string, message: string): void => {
    problems.push({ kind, name, message });
  };

  if (typeof skin !== 'object' || skin === null) {
    return { ok: false, problems: [{ kind: 'id', name: '<root>', message: 'skin is not an object' }] };
  }
  const s = skin as Partial<Skin>;

  if (typeof s.id !== 'string' || !(SKIN_IDS as readonly string[]).includes(s.id)) {
    push('id', String(s.id), `skin id must be one of ${SKIN_IDS.join(', ')}`);
  }
  if (typeof s.label !== 'string' || s.label.length === 0) {
    push('label', 'label', 'skin must carry a non-empty label');
  }

  const components = s.components as Record<string, unknown> | undefined;
  for (const name of SKIN_COMPONENT_NAMES) {
    if (typeof components?.[name] !== 'function') {
      push('component', name, `skin is missing the ${name} component (§6.1)`);
    }
  }

  const policy = s.policy as Record<string, unknown> | undefined;
  for (const name of POLICY_METHOD_NAMES) {
    if (typeof policy?.[name] !== 'function') {
      push('policy', name, `skin policy is missing ${name}() (§5.4)`);
    }
  }

  if (!s.keymap || !Array.isArray(s.keymap.bindings) || s.keymap.bindings.length === 0) {
    push('keymap', 'keymap', 'skin must declare a keymap with at least one binding (§6.4)');
  } else if (s.keymap.id !== s.id) {
    push('keymap', 'keymap.id', `keymap id '${s.keymap.id}' does not match skin id '${s.id}'`);
  }

  if (typeof s.tokens !== 'string' || s.tokens.length === 0) {
    push('token', 'tokens', 'skin must supply its tokens.css text (§6.2)');
  } else {
    for (const name of missingTokens(s.tokens)) {
      push('token', name, `skin does not define the §6.2 token ${name}`);
    }
  }

  const sounds = s.sounds as Record<string, unknown> | undefined;
  for (const id of SOUND_IDS) {
    const recipe = sounds?.[id] as { tones?: unknown } | undefined;
    if (!recipe || !Array.isArray(recipe.tones)) {
      push('sound', id, `skin is missing a sound recipe for '${id}' (§15)`);
    }
  }

  return problems.length === 0 ? { ok: true } : { ok: false, problems };
}

/** Convenience for tests and error notifications. */
export function describeSkinProblems(validation: SkinValidation): string {
  return validation.ok ? '' : validation.problems.map((p) => p.message).join('; ');
}
